// Read-only, share-able trip view.
// Token is opaque to the frontend; backend (/api/share/{token}) returns
// the booking projection without requiring authentication.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Share2, MapPin, Calendar } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const apiBase = process.env.REACT_APP_BACKEND_URL || '';

const TripShare = () => {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${apiBase}/api/share/${token}`)
      .then((r) => setBooking(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Trip not found.'))
      .finally(() => setLoading(false));
  }, [token]);

  const shareTo = (channel) => {
    const url = window.location.href;
    const text = `Check out my upcoming trip on Elite Travo: ${booking?.headline || ''}`;
    const targets = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      twitter:  `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    };
    window.open(targets[channel], '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading…</div>;
  if (error) return (
    <div className="p-10 text-center">
      <p className="font-bold text-gray-900 mb-1">This trip link is no longer valid.</p>
      <p className="text-sm text-gray-500">{error}</p>
    </div>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="bg-gradient-to-r from-red-600 to-red-800 text-white p-5 rounded-t-xl">
            <p className="text-xs uppercase tracking-wider opacity-80">Shared trip · view-only</p>
            <h1 className="text-2xl font-bold mt-1" data-testid="trip-share-headline">{booking.headline}</h1>
            <p className="text-sm opacity-90 mt-1 flex items-center gap-1">
              <MapPin className="h-4 w-4" /> {booking.location}
            </p>
          </div>
          <CardContent className="p-5 space-y-3 text-sm">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <Calendar className="h-4 w-4 text-red-600" />
              <span><b>{booking.date_label}</b></span>
            </div>
            {booking.summary?.map((s, i) => (
              <div key={i} className="flex justify-between border-t border-gray-100 dark:border-gray-800 pt-2">
                <span className="text-gray-500">{s.label}</span>
                <span className="font-semibold text-gray-900 dark:text-white">{s.value}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="mt-4">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Share to</p>
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" onClick={() => shareTo('whatsapp')} data-testid="share-whatsapp">WhatsApp</Button>
            <Button variant="outline" onClick={() => shareTo('telegram')} data-testid="share-telegram">Telegram</Button>
            <Button variant="outline" onClick={() => shareTo('twitter')} data-testid="share-twitter">X / Twitter</Button>
          </div>
        </div>

        <div className="mt-6 p-4 bg-white dark:bg-gray-900 border border-dashed border-red-300 rounded-xl text-center">
          <p className="text-sm font-bold text-gray-900 dark:text-white">Loved this? Plan your own trip on Elite Travo.</p>
          <Button asChild className="mt-3 bg-red-600 hover:bg-red-700 text-white" data-testid="cta-start-planning">
            <a href="/">Start planning →</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TripShare;
